/**
 * Build a decorative panel covering a section of a wall — used to
 * close the gap on the sink wall between the sink cabinet's near-corner
 * end and the corner, when the perpendicular wall holds a single-sided
 * corner cabinet.
 *
 * Visually it reads like a 24"-deep lower cabinet front so the cabinet
 * run looks continuous, but the panel's collision body is intentionally
 * thin (~1") so it doesn't intrude into the perpendicular corner
 * cabinet's footprint inside the corner box. `skipCollisionCheck: true`
 * keeps it out of fixture collision detection regardless.
 *
 * Height matches a lower cabinet (no countertop). The countertop run
 * for this wall spans over the panel just like it spans over a cabinet.
 */

const { KITCHEN_CABINET_DIMENSIONS, INCHES_TO_METERS } = require('./constants');
const { getWallAxes } = require('./wallMath');

const PANEL_DEPTH = 1 * INCHES_TO_METERS;

function buildBlankPanel({
  wall,
  segment,
  floorY,
  fullDepth = false,
  filler = false,
  // The depth of the cabinets the panel stands among, for `fullDepth`: a run
  // built at another depth than the standard (an island row set to 26") must
  // not close its line with a 24" piece — the panel would stand short of its
  // neighbours and its counter item would break the slab (`groupIntoRuns`).
  depth = KITCHEN_CABINET_DIMENSIONS.depth,
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  if (segment.end - segment.start <= 0) return null;
  const ry = axes.wallRotationY;

  const segmentMid = (segment.start + segment.end) / 2;
  const segmentWidth = segment.end - segment.start;
  const panelHeight = KITCHEN_CABINET_DIMENSIONS.height;

  const surfacePoint = {
    x: axes.start.x + segmentMid * axes.wallDir.x,
    z: axes.start.z + segmentMid * axes.wallDir.z,
  };

  // Two panel modes:
  //   fullDepth=false (default): a 1"-thin panel at the cabinet front
  //     face. Used at the corner-side of single-sided corner cabinets,
  //     where a 24"-deep body would intrude into the perpendicular
  //     cabinet's footprint inside the corner box.
  //   fullDepth=true: a 24"-deep filler that occupies the whole
  //     cabinet-body footprint. Used by the filler pass to bridge
  //     small gaps (< 6") between adjacent perpendicular cabinet /
  //     appliance bodies — without the full depth the gap reads as
  //     a hole in the cabinet run.
  const cabinetDepth = depth;
  const bodyDepth = fullDepth ? cabinetDepth : PANEL_DEPTH;
  const offsetFromWall = fullDepth
    ? cabinetDepth / 2
    : cabinetDepth - PANEL_DEPTH / 2;
  const center = {
    x: surfacePoint.x + offsetFromWall * axes.wallNormal.x,
    y: floorY + panelHeight / 2,
    z: surfacePoint.z + offsetFromWall * axes.wallNormal.z,
  };

  const stamp = `${wall.id}_${Math.round(segment.start * 1000)}`;

  return {
    id: `blank_panel_${stamp}`,
    type: 'blank-panel',
    label: 'Blank Panel',
    // `filler` marks a panel that CLOSES A GAP in a cabinet line — a strip
    // between two bodies, or the end piece beside an appliance. Those are the
    // pieces `fillRun` re-emits on an edit, so a derived run must own them
    // (`toRuns.OWNABLE_BAY_TYPES`) and replace them; without the mark the
    // re-solve stacked its own filler bays alongside the generator's and the
    // run grew a doubled strip. A panel WITHOUT it — the 1"-deep corner cover,
    // a peninsula's back panel — is not a gap in a line and stays the
    // generator's.
    category: { 'blank-panel': true, ...(filler ? { filler: true } : {}) },
    center,
    dimensions: {
      x: segmentWidth,
      y: panelHeight,
      z: bodyDepth,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
    range: { start: segment.start, end: segment.end },
  };
}

module.exports = { buildBlankPanel, PANEL_DEPTH };
